/* Crumb & Copper – interactions for the rm-* sections:
   hero slider, tabs, lazy product feeds, product rails, scroll reveal, count-up stats
   and the sticky add-to-cart bar. The header lives in rm-header.js. */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var feedCounter = 0;

  function each(scope, selector, fn) {
    Array.prototype.forEach.call(scope.querySelectorAll(selector), fn);
  }
  function claim(el) {
    if (el.dataset.rmReady) return false;
    el.dataset.rmReady = '1';
    return true;
  }

  /* ---------- Hero slider ---------- */
  function initHero(root) {
    if (!claim(root)) return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    var bars = Array.prototype.slice.call(root.querySelectorAll('[data-go]'));
    var counter = root.querySelector('[data-count-current]');
    if (slides.length < 2) return;
    var index = 0;
    var autoplay = root.dataset.autoplay === 'true' && !reduceMotion && !designMode;

    function go(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
      });
      bars.forEach(function (bar, i) {
        bar.classList.toggle('is-active', i === index);
        bar.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
    }

    if (autoplay) {
      root.classList.add('rm-hero--auto');
      root.addEventListener('animationend', function (event) {
        if (event.animationName === 'rm-fill' && event.target.parentElement.classList.contains('is-active')) go(index + 1);
      });
      var pause = function () { root.classList.add('is-paused'); };
      var resume = function () { if (!root.contains(document.activeElement)) root.classList.remove('is-paused'); };
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', function () { root.classList.remove('is-paused'); });
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', function () { setTimeout(resume, 0); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          root.classList.toggle('is-offscreen', !entries[0].isIntersecting);
          if (!entries[0].isIntersecting) root.classList.add('is-paused');
          else if (!root.matches(':hover')) root.classList.remove('is-paused');
        }).observe(root);
      }
    }

    var prev = root.querySelector('[data-prev]');
    var next = root.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    bars.forEach(function (bar, i) { bar.addEventListener('click', function () { go(i); }); });
    root.addEventListener('keydown', function (event) {
      if (event.target.closest('input, textarea')) return;
      if (event.key === 'ArrowRight') go(index + 1);
      if (event.key === 'ArrowLeft') go(index - 1);
    });
    root.addEventListener('shopify:block:select', function (event) {
      var i = slides.indexOf(event.target);
      if (i > -1) go(i);
    });
    go(0);
  }

  /* ---------- Lazy product feeds ----------
     A feed element carries data-feed="/collections/types?q=…". Its products are rendered by
     sections/rm-type-feed.liquid through the Section Rendering API, so no extra collections are needed. */
  var feedObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          feedObserver.unobserve(entry.target);
          loadFeed(entry.target);
        });
      }, { rootMargin: '700px 0px' })
    : null;

  function initFeed(el) {
    if (!claim(el)) return;
    if (el.closest('[hidden]')) return;
    if (feedObserver) feedObserver.observe(el);
    else loadFeed(el);
  }

  function loadFeed(el) {
    if (el.dataset.rmLoaded) return;
    el.dataset.rmLoaded = '1';
    var url = el.dataset.feed;
    if (!url) return;
    var limit = parseInt(el.dataset.limit || '8', 10);
    var exclude = el.dataset.exclude || '';
    var request = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=rm-type-feed';

    fetch(request, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Feed request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = Array.prototype.slice.call(doc.querySelectorAll('.rm-feed > li'));
        if (exclude) items = items.filter(function (li) { return li.dataset.handle !== exclude; });
        items = items.slice(0, limit);
        el.innerHTML = '';
        if (!items.length) {
          el.classList.add('is-empty');
          var host = el.closest('[data-hide-when-empty]');
          if (host) host.hidden = true;
          else el.insertAdjacentHTML('afterend', '<p class="rm-feed-empty">' + (el.dataset.emptyText || '') + '</p>');
          return;
        }
        feedCounter += 1;
        items.forEach(function (li, i) {
          each(li, 'form[id]', function (form) { form.id = form.id + '-f' + feedCounter; });
          li.style.setProperty('--i', i);
          if (!reduceMotion) li.classList.add('rm-pop');
          el.appendChild(document.importNode(li, true));
        });
        el.classList.add('is-loaded');
        el.dispatchEvent(new CustomEvent('rm:feed-loaded', { bubbles: true }));
      })
      .catch(function () {
        el.classList.add('is-error');
      });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    if (!claim(root)) return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    function select(tab, focus) {
      tabs.forEach(function (other) {
        var active = other === tab;
        other.setAttribute('aria-selected', active ? 'true' : 'false');
        other.tabIndex = active ? 0 : -1;
        var panel = document.getElementById(other.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !active;
        if (active) each(panel, '[data-feed]', function (feed) { loadFeed(feed); });
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        var step = event.key === 'ArrowRight' ? 1 : -1;
        select(tabs[(i + step + tabs.length) % tabs.length], true);
      });
    });
    root.addEventListener('shopify:block:select', function (event) {
      var tab = event.target.closest('[role="tab"]');
      if (tab) select(tab, false);
    });
  }

  /* ---------- Product rails ---------- */
  function initRail(root) {
    if (!claim(root)) return;
    var track = root.querySelector('[data-track]');
    var prev = root.querySelector('[data-prev]');
    var next = root.querySelector('[data-next]');
    var bar = root.querySelector('[data-progress]');
    if (!track) return;
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft < 8;
      if (next) next.disabled = track.scrollLeft > max - 8;
      if (bar) {
        var ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        bar.style.width = Math.min(100, ratio * 100) + '%';
        bar.style.transform = 'translateX(' + (max > 0 ? (track.scrollLeft / max) * ((1 / Math.max(ratio, 0.01)) - 1) * 100 : 0) + '%)';
        bar.parentElement.hidden = ratio >= 0.99;
      }
    }
    function move(direction) {
      track.scrollBy({ left: direction * track.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });
    track.addEventListener('scroll', update, { passive: true });
    root.addEventListener('rm:feed-loaded', function () { requestAnimationFrame(update); });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Scroll reveal ---------- */
  var revealObserver = !reduceMotion && !designMode && 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;

  function initReveal(el) {
    if (!claim(el)) return;
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-in');
  }

  /* ---------- Count-up numbers ---------- */
  function initCount(el) {
    if (!claim(el)) return;
    var raw = el.textContent.trim();
    var target = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(target) || reduceMotion || designMode || !('IntersectionObserver' in window)) return;
    var decimals = (raw.split('.')[1] || '').length;
    var format = function (value) {
      return value.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };
    el.textContent = format(0);
    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      var start = null;
      var duration = 1600;
      function step(time) {
        if (start === null) start = time;
        var progress = Math.min((time - start) / duration, 1);
        el.textContent = format(target * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = format(target);
      }
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    observer.observe(el);
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initSticky(bar) {
    if (!claim(bar)) return;
    var submit = document.querySelector('.product-form__submit');
    var trigger = bar.querySelector('[data-rm-sticky-add]');
    if (!submit || !trigger || !('IntersectionObserver' in window)) return;
    bar.inert = true;
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
    }).observe(submit);
    trigger.addEventListener('click', function () {
      if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
        submit.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        return;
      }
      submit.click();
    });
  }

  function init(scope) {
    each(scope, '[data-rm-hero]', initHero);
    each(scope, '[data-rm-tabs]', initTabs);
    each(scope, '[data-feed]', initFeed);
    each(scope, '[data-rm-rail]', initRail);
    each(scope, '[data-reveal]', initReveal);
    each(scope, '[data-count]', initCount);
    each(scope, '[data-rm-sticky]', initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();